"use client";

import {
  clearQueryPerf,
  getQueryPerfSnapshot,
  subscribeQueryPerf,
} from "@/utils/perf/queryPerf";
import { useEffect, useState, useSyncExternalStore } from "react";

/**
 * Dev-only performance overlay for React Query fetch durations. Gated by
 * `?debugPerf=true` + `NODE_ENV === "development"` (same pattern as
 * `PortalDebugger`). Lists recent query round-trips; cross-reference each
 * `durationMs` with the `[rpc] … Xms` server console log to separate DB time
 * from network + serialization.
 */
export function QueryPerfOverlay() {
  const [visible, setVisible] = useState(false);
  const records = useSyncExternalStore(
    subscribeQueryPerf,
    getQueryPerfSnapshot,
    getQueryPerfSnapshot,
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (
      process.env.NODE_ENV !== "development" &&
      process.env.NEXT_PUBLIC_PERF_DEBUG !== "true"
    ) {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    setVisible(params.get("debugPerf") === "true");
  }, []);

  if (!visible) return null;

  const durations = records.map((r) => r.durationMs);
  const slowest = durations.length ? Math.max(...durations) : 0;

  return (
    <div
      style={{
        position: "fixed",
        bottom: 8,
        left: 8,
        zIndex: 2147483647,
        width: 380,
        maxHeight: "45vh",
        display: "flex",
        flexDirection: "column",
        background: "rgba(17,24,39,0.94)",
        color: "#e5e7eb",
        border: "1px solid #374151",
        borderRadius: 8,
        fontSize: 11,
        fontFamily: "ui-monospace, monospace",
        boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "6px 8px",
          borderBottom: "1px solid #374151",
        }}
      >
        <span style={{ fontWeight: 600 }}>
          Query perf · {records.length} · max {slowest}ms
        </span>
        <button
          type="button"
          onClick={() => clearQueryPerf()}
          style={{
            background: "#374151",
            color: "#e5e7eb",
            border: "none",
            borderRadius: 4,
            padding: "2px 8px",
            cursor: "pointer",
          }}
        >
          clear
        </button>
      </div>
      <div style={{ overflowY: "auto", padding: 4 }}>
        {records.length === 0 ? (
          <div style={{ padding: 8, opacity: 0.6 }}>Sin queries aún…</div>
        ) : (
          records.map((r) => (
            <div
              key={r.id}
              style={{
                display: "grid",
                gridTemplateColumns: "56px 1fr auto",
                gap: 6,
                padding: "3px 4px",
                borderBottom: "1px solid rgba(55,65,81,0.5)",
                alignItems: "baseline",
              }}
            >
              <span
                style={{
                  fontWeight: 700,
                  textAlign: "right",
                  color:
                    r.status === "error"
                      ? "#f87171"
                      : r.durationMs > 800
                        ? "#fbbf24"
                        : "#34d399",
                }}
              >
                {r.durationMs}ms
              </span>
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={r.keyLabel}
              >
                {r.keyLabel}
              </span>
              <span style={{ opacity: 0.6 }}>
                {r.rowCount == null ? "" : `${r.rowCount} rows`}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
